// src/components/inspectionBot/Summary.jsx

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SignInHeader from "../auth/SigninHeader";
import Navigation from "../layout/Navigation";

const SummaryItem = ({ label, value, isDarkMode }) => (
  <div className={`flex justify-between items-center py-4 border-b transition-colors duration-200 ${
    isDarkMode ? 'border-gray-600' : 'border-gray-200'
  }`}>
    <span className={`text-sm font-medium ${
      isDarkMode ? 'text-gray-400' : 'text-gray-600'
    }`}>
      {label}
    </span>
    <span className={`font-semibold text-right max-w-xs truncate ${
      isDarkMode ? 'text-white' : 'text-gray-900'
    }`} title={value || 'Not Set'}>
      {value || <span className="text-red-500">Not Set</span>}
    </span>
  </div>
);

const Summary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [summaryData, setSummaryData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [apiResponse, setApiResponse] = useState({ message: '', type: '' });

  useEffect(() => {
    // Load theme
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(savedTheme === 'dark' || (!savedTheme && prefersDark));

    // Load user
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }

    // Check authentication
    const token = localStorage.getItem('token');
    setAuthenticated(!!token);

    if (!token) {
      navigate('/signin');
      return;
    }

    // Combine data from location state and localStorage as fallback
    const data = {
      selectedTemplate: location.state?.selectedTemplate || localStorage.getItem('selectedTemplate'),
      group: location.state?.group || localStorage.getItem('group'),
      type: location.state?.type || localStorage.getItem('type'),
      subtype: location.state?.subtype || localStorage.getItem('subtype'),
      category: location.state?.category || localStorage.getItem('category'),
      selectedG6Type: location.state?.selectedG6Type || localStorage.getItem('selectedG6Type'),
      selectedInspectionResult: location.state?.selectedWorkflowStatus || localStorage.getItem('selectedInspectionResult'),
      selectedContactType: location.state?.selectedContactType || localStorage.getItem('selectedContactType'),
    };
    
    setSummaryData(data);

    // Validate required data
    const requiredFields = ['selectedTemplate', 'group', 'type', 'subtype', 'category', 'selectedG6Type'];
    const missingFields = requiredFields.filter(field => !data[field]);
    
    if (missingFields.length > 0) {
      setApiResponse({ 
        message: `Missing required configuration: ${missingFields.join(', ')}. Please complete the setup process.`, 
        type: 'error' 
      });
    }
  }, [location.state, navigate]);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  const handleBackToStart = () => {
    // Clear all stored data
    const keysToRemove = [
      'selectedTemplate', 'group', 'type', 'subtype', 'category',
      'selectedG6Type', 'selectedInspectionResult', 'selectedContactType'
    ];
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    navigate('/mainscreen');
  };

  const handleEditStep = (step) => {
    switch (step) {
      case 'template':
        navigate('../template-viewer', { state: summaryData });
        break;
      case 'recordType':
        navigate('../record-types', { state: summaryData });
        break;
      case 'trigger':
        navigate('../triggerlocation', { state: summaryData });
        break;
      case 'inspection':
        navigate('../taskselection', { state: summaryData });
        break;
      case 'result':
        navigate('../inspection-result', { state: summaryData });
        break;
      case 'contact':
        navigate('../contact-types', { state: summaryData });
        break;
      default:
        break;
    }
  };

  const handleActivate = async () => {
    setIsSubmitting(true);
    setApiResponse({ message: '', type: '' });
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setApiResponse({ message: 'Authentication required. Please sign in.', type: 'error' });
        navigate('/signin');
        return;
      }

      // Validate all required data before submitting
      const requiredData = {
        selectedTemplate: summaryData.selectedTemplate,
        recordType: `${summaryData.group}/${summaryData.type}/${summaryData.subtype}/${summaryData.category}`,
        inspectionType: summaryData.selectedG6Type,
        inspectionResult: summaryData.selectedInspectionResult,
        contactType: summaryData.selectedContactType
      };

      const missingFields = Object.entries(requiredData)
        .filter(([key, value]) => !value)
        .map(([key]) => key);

      if (missingFields.length > 0) {
        setApiResponse({ 
          message: `Cannot activate: Missing ${missingFields.join(', ')}`, 
          type: 'error' 
        });
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/trigger-gateway`, 
        {
          configurationData: summaryData,
          activationTimestamp: new Date().toISOString()
        },
        { 
          headers: { 
            Authorization: token,
            'Content-Type': 'application/json'
          } 
        }
      );

      if (response.data.success) {
        setApiResponse({ 
          message: response.data.message || 'Inspection Bot activated successfully! Your communication rules are now active.', 
          type: 'success' 
        });
        
        // Clear the stored configuration data after successful activation
        setTimeout(() => {
          const keysToRemove = [
            'selectedTemplate', 'group', 'type', 'subtype', 'category',
            'selectedG6Type', 'selectedInspectionResult', 'selectedContactType'
          ];
          keysToRemove.forEach(key => localStorage.removeItem(key));
        }, 3000);
      } else {
        setApiResponse({ 
          message: response.data.message || 'Activation failed. Please try again.', 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error('Error activating inspection bot:', error);
      
      let errorMessage = 'An unexpected error occurred during activation.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please sign in again.';
        setTimeout(() => navigate('/signin'), 2000);
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setApiResponse({ message: errorMessage, type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!authenticated) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-main-dark' : 'bg-gray-100'
      }`}>
        <div className="text-center p-6 rounded-xl bg-red-50 text-red-600 max-w-md">
          <p className="text-lg font-medium mb-4">Please sign in to continue</p>
          <button
            onClick={() => navigate('/signin')}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col min-h-screen transition-colors duration-300 ${
      isDarkMode ? "bg-main-dark text-white" : "bg-gray-100 text-gray-900"
    }`}>
      <SignInHeader 
        user={user} 
        isLoggedIn={authenticated} 
        isDarkMode={isDarkMode} 
        toggleDarkMode={toggleDarkMode} 
      />
      <Navigation 
        activeTab="My Apps" 
        setActiveTab={() => {}} 
        isDarkMode={isDarkMode} 
        toggleDarkMode={toggleDarkMode}
      />
      
      <div className="flex flex-col items-center pt-[140px] pb-10 px-5 flex-grow">
        <div className={`w-full max-w-3xl rounded-xl shadow-lg p-8 ${
          isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-3">Configuration Summary</h1>
            <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Review your inspection bot configuration below. Click "Activate" to enable automated communications.
            </p>
          </div>
          
          {/* Configuration Details */}
          <div className="space-y-0 mb-8">
            <SummaryItem 
              label="Communication Template" 
              value={summaryData.selectedTemplate}
              isDarkMode={isDarkMode}
            />
            <SummaryItem 
              label="Record Type" 
              value={summaryData.group && summaryData.type ? 
                `${summaryData.group} / ${summaryData.type} / ${summaryData.subtype} / ${summaryData.category}` : 
                null
              }
              isDarkMode={isDarkMode}
            />
            <SummaryItem 
              label="Triggering Inspection" 
              value={summaryData.selectedG6Type}
              isDarkMode={isDarkMode}
            />
            <SummaryItem 
              label="Triggering Result Status" 
              value={summaryData.selectedInspectionResult}
              isDarkMode={isDarkMode}
            />
            <SummaryItem 
              label="Recipient Contact Type" 
              value={summaryData.selectedContactType}
              isDarkMode={isDarkMode}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={handleBackToStart}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                isDarkMode 
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Start Over
            </button>
            
            <button
              onClick={handleActivate}
              disabled={isSubmitting}
              className={`px-8 py-3 rounded-lg font-semibold text-white transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-green-500/30 ${
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-lg hover:shadow-xl'
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Activating...</span>
                </div>
              ) : (
                'Activate Inspection Bot'
              )}
            </button>
          </div>

          {/* Response Messages */}
          {apiResponse.message && (
            <div className={`mt-6 p-4 rounded-lg text-center ${
              apiResponse.type === 'success' 
                ? (isDarkMode ? 'bg-green-900/50 text-green-300 border border-green-700' : 'bg-green-100 text-green-800 border border-green-200')
                : (isDarkMode ? 'bg-red-900/50 text-red-300 border border-red-700' : 'bg-red-100 text-red-800 border border-red-200')
            }`}>
              <div className="flex items-center justify-center space-x-2">
                <span className="text-lg">
                  {apiResponse.type === 'success' ? '✅' : '❌'}
                </span>
                <span className="font-medium">{apiResponse.message}</span>
              </div>
              
              {apiResponse.type === 'success' && (
                <button
                  onClick={handleBackToStart}
                  className="mt-3 text-sm underline hover:no-underline"
                >
                  Create Another Bot
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Summary;